// Note: some ad blockers don't delete pageDataTracker,
// they change the type of trackEvent
const trackEvent = (...args) =>
  typeof window.pageDataTracker !== 'undefined' &&
  typeof window.pageDataTracker.trackEvent === 'function' &&
  window.pageDataTracker.trackEvent(...args);

const getPositionInResults = (currentPage, position) => {
  const isFromSearch =
    typeof currentPage === 'number' &&
    typeof position === 'number';

  if (!isFromSearch) {
    return '';
  }

  const indexPosition = position === 0
    ? 1
    : position + 1;

  const positionInResults = currentPage === 1
    ? indexPosition
    : ((currentPage - 1) * 20) + indexPosition;

  return positionInResults.toString(); // For whatever reason must be a string...
};

const getReaders = (doc, stats) =>
  (stats && stats.totalReaders) || doc.readerCount || 'not-available';

const getCitations = (doc, stats) =>
  (stats && stats.totalCitations) || 'not-available';

const getId = doc =>
  (doc.identifiers && doc.identifiers.doi) || doc.id;

const yes = condition =>
  (condition ? 'yes' : 'no');

export function trackAddedReference({
  doc,
  stats,
  currentPage,
  position,
  isSuggested,
}) {
  const readers = getReaders(doc, stats);
  const citations = getCitations(doc, stats);

  const payload = {
    content: [{
      id: `md:article:id:${getId(doc)}`,
      type: 'md:article:scope-abstract',
      format: 'mime-html',
      title: doc.title,
      position: getPositionInResults(currentPage, position),
      detail: `md:article:has_pdf:${yes(doc.hasPdf)}:readers:${readers}:citations:${citations}:publication_year:${doc.year}:suggested:${yes(isSuggested)}`,
    }],
  };

  trackEvent('contentAddition', payload);
}

export function trackSearchResultTitleClick({
  doc,
  stats,
  criteria,
  position,
  currentPage,
  isSuggested,
}) {
  const readers = getReaders(doc, stats);
  const citations = getCitations(doc, stats);

  const payload = {
    content: [{
      id: `md:article:id:${getId(doc)}`,
      type: 'md:article:scope-abstract',
      format: 'mime-html',
      title: doc.title,
      detail: `md:article:has_pdf:${yes(doc.hasPdf)}:readers:${readers}:citations:${citations}:publication_year:${doc.year}:suggested:${yes(isSuggested)}`,
    }],
    search: {
      criteria,
      resultsPosition: getPositionInResults(currentPage, position),
      type: 'md:article',
    }
  };

  trackEvent('searchResultsClick', payload);
}

export function trackGetFullTextClick({
  doc,
  stats,
  position,
  currentPage,
  isSuggested,
}) {
  const readers = getReaders(doc, stats);
  const citations = getCitations(doc, stats);

  const payload = {
    content: [{
      id: `md:article:id:${getId(doc)}`,
      type: 'md:article:scope-full',
      format: 'mime-html',
      title: doc.title,
      position: getPositionInResults(currentPage, position),
      detail: `md:article:has_pdf:${yes(doc.hasPdf)}:readers:${readers}:citations:${citations}:publication_year:${doc.year}:suggested:${yes(isSuggested)}`,
    }],
  };

  trackEvent('linkOut', payload);
}

export function trackUpdatedSearchResults({
  criteria,
  currentPage,
  total,
  limit,
}) {
  const payload = {
    search: {
      criteria,
      currentPage,
      totalResults: total,
      resultsPerPage: limit,
      type: 'md:search:catalog'
    }
  };

  trackEvent('searchResultsUpdated', payload);
}

export function trackSearchResultsPageView() {
  const {
    page = {},
    visitor = {},
  } = window.pageData || {};

  const {
    businessUnit,
    environment,
    language,
    noTracking,
  } = page;

  const payload = {
    visitor,
    page: {
      businessUnit,
      environment,
      language,
      noTracking,
      loadTimestamp: '0',
      productName: 'md',
      name: 'search:papers:search-query',
      type: 'sp-st',
    },
  };

  trackEvent('newPage', payload);
}

export function trackArticlePageView() {
  const {
    page = {},
    visitor = {},
  } = window.pageData || {};

  const {
    businessUnit,
    environment,
    language,
    noTracking,
  } = page;

  const payload = {
    visitor,
    page: {
      businessUnit,
      environment,
      language,
      noTracking,
      loadTimestamp: '0',
      productName: 'md',
      name: 'papers:individual-entry',
      type: 'cp-ca',
    },
  };

  trackEvent('newPage', payload);
}

export function trackViewPdfClick({
  doc,
  stats,
}) {
  const readers = getReaders(doc, stats);
  const citations = getCitations(doc, stats);

  const payload = {
    content: [{
      id: `md:article:id:${getId(doc)}`,
      type: 'md:article:scope-full',
      format: 'mime-pdf',
      title: doc.title,
      detail: `md:article:has_pdf:yes:readers:${readers}:citations:${citations}:publication_year:${doc.year}`,
    }],
  };

  trackEvent('contentView', payload);
}

export function trackSignInToAddToLibrary({
  doc,
  stats,
}) {
  const readers = getReaders(doc, stats);
  const citations = getCitations(doc, stats);

  const payload = {
    content: [{
      id: `md:article:id:${getId(doc)}`,
      type: 'md:article:scope-abstract',
      format: 'mime-html',
      title: doc.title,
      detail: `md:article:has_pdf:${yes(doc.hasPdf)}:readers:${readers}:citations:${citations}:publication_year:${doc.year}`,
    }],
    link: {
      location: 'main',
      name: 'Sign in to add to library'
    },
  };

  trackEvent('navigationClick', payload);
}

export function trackSignInToViewPdf({
  doc,
  stats,
}) {
  const readers = getReaders(doc, stats);
  const citations = getCitations(doc, stats);

  const payload = {
    content: [{
      id: `md:article:id:${getId(doc)}`,
      type: 'md:article:scope-full',
      format: 'mime-pdf',
      title: doc.title,
      detail: `md:article:has_pdf:yes:readers:${readers}:citations:${citations}:publication_year:${doc.year}`,
    }],
    link: {
      location: 'main',
      name: 'Sign in to view PDF'
    }
  };

  trackEvent('navigationClick', payload);
}

export function trackCopyToClipboard({
  doc,
  stats,
  citationStyle,
}) {
  const readers = getReaders(doc, stats);
  const citations = getCitations(doc, stats);

  const payload = {
    content: [{
      id: `md:article:id:${getId(doc)}`,
      type: 'md:article:scope-abstract',
      format: 'mime-html',
      title: doc.title,
      detail: `md:article:has_pdf:${yes(doc.hasPdf)}:readers:${readers}:citations:${citations}:publication_year:${doc.year}:suggested:no:citation_style:${citationStyle}`,
    }],
  };

  trackEvent('copyToClipboard', payload);
}

const getFullTextLink = (doc, isAuthenticated) => {
  if (isAuthenticated && doc.isEntitledToPdf) {
    return 'viewpdf';
  } else if (doc.isEntitledToPdf) {
    return 'signintoviewpdf';
  } else if (doc.identifiers && doc.identifiers.doi) {
    return 'getfulltextatjournal';
  }

  return 'not-available';
};

export function trackDocumentInView({
  doc,
  stats,
  isAuthenticated,
}) {
  const readers = getReaders(doc, stats);
  const citations = getCitations(doc, stats);
  const fullTextLink = getFullTextLink(doc, isAuthenticated);

  const payload = {
    content: [{
      id: `md:article:id:${getId(doc)}`,
      type: 'md:article:scope-abstract',
      format: 'mime-html',
      title: doc.title,
      detail: `md:article:has_pdf:${yes(doc.hasPdf)}:readers:${readers}:citations:${citations}:full_text_link:${fullTextLink}`,
    }],
    cta: {
      ids: ['md:article']
    }
  };

  trackEvent('ctaImpression', payload);
}



// WEBPACK FOOTER //
// ./src/universal/ui/utils/adobe.js